import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { resultPageWidgets } from '~/config/widgets/resultPageWidgets';
import { AppRoute } from '@agentselly/immodossier-shared';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import {
  createFileRoute,
  useCanGoBack,
  useRouter,
} from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { ActionButton } from '~/components/button/ActionButton';
import { clsx } from 'clsx';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { useAppParams } from '~/helper/useAppParams';
import { trackEvent } from '@agentselly/analytics';
import { useV3Redesign } from '~/helper/useV3Redesign';
import {
  V3ResultCalc,
  V3ResultError,
  V3ResultPage,
} from '~/components/public/V3ResultPage';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PUBLIC_EVALUATION_RESULT_$SESSIONID,
)({
  component: Body,
  staleTime: 0,
});

function Body() {
  const { t } = useTranslate();

  const params = useAppParams(Route);
  const v3 = useV3Redesign();

  const firstIntervalRequest = Date.now();

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent('immodossier evaluation result shown');
  }, []);

  const dossierCreatedTrackedRef = useRef(false);
  const sawAbsentWidgetPropsRef = useRef(false);
  const createdBannerRef = useRef(false);

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_PUBLIC_EVALUATION_RESULT_$SESSIONID,
    {
      refetchInterval: (context: any) => {
        const widgetProps = context?.state?.data?.widgetProps;

        if (widgetProps && Object.keys(widgetProps).length) {
          return false;
        }

        const diff = Date.now() - firstIntervalRequest;

        if (diff <= 10000) {
          // Retry every 1 second for the first 10 seconds
          return 1000;
        }

        if (diff <= 30000) {
          // Retry every 5 seconds for the next 20 seconds
          return 5000;
        }

        // Retry every 60 seconds after 30 seconds
        return 60000;
      },
      params: params,
    },
  );

  if (query.isLoading) {
    return v3 ? <V3ResultCalc /> : <LoadingWidget />;
  }

  if (query.isError || !query.data) {
    return v3 ? (
      <V3Error
        error="unknown"
        sessionId={params.sessionId}
      />
    ) : (
      <ErrorWidget error={query.error} />
    );
  }

  if (v3 && query.data.error) {
    return (
      <V3Error
        error={query.data.error}
        sessionId={params.sessionId}
        address={query.data.errorInputFullAddress}
      />
    );
  }

  if (query.data.error) {
    switch (query.data.error) {
      case 'already_known':
      case 'dossier_exists':
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            link="/"
            linkLabel={t(
              'calculator_result_page_already_known_error_message_link_text',
            )}
          >
            <p>
              {t(
                'calculator_result_page_already_known_error_message_prefix_text',
              )}{' '}
              <a
                href="/apps/immodossier-client/app/routes/$lang.$companyId/public"
                className="text-blue-600 hover:text-blue-800 underline"
                data-track="immodossier valiant result already known link"
              >
                {t(
                  'calculator_result_page_already_known_error_message_link_text',
                )}
              </a>{' '}
              {t(
                'calculator_result_page_already_known_error_message_postfix_text',
              )}
            </p>
          </ErrorCard>
        );
      case 'invalid_email':
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            fallbackLink="/"
            linkLabel={t('calculator_result_page_back_link')}
          >
            <p>{t('calculator_result_page_invalid_email_error_message')}</p>
          </ErrorCard>
        );
      case 'invalid_address':
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            fallbackLink="/"
            linkLabel={t('calculator_result_page_back_link')}
          >
            <p className="whitespace-pre-wrap">
              {t('calculator_result_page_invalid_address_error_message', {
                address: query.data.errorInputFullAddress || '',
              })}
            </p>
          </ErrorCard>
        );
      default:
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            link="/"
            linkLabel={t('calculator_result_page_login_link')}
          >
            <p>{t('calculator_result_page_unknown_error_message')}</p>
            <p className="my-2">
              {t('calculator_result_page_unknown_session_id', {
                value: params.sessionId || 'error',
              })}
            </p>
            <p>
              <a
                href="mailto:support@agentselly.ch"
                className="underline text-blue-600"
                data-track="immodossier valiant result support mailto"
              >
                support@agentselly.ch
              </a>
            </p>
          </ErrorCard>
        );
    }
  }

  const hasWidgetProps = Boolean(
    query.data.widgetProps && Object.keys(query.data.widgetProps).length,
  );
  if (hasWidgetProps && sawAbsentWidgetPropsRef.current) {
    createdBannerRef.current = true;
  }
  const dossierCreated = createdBannerRef.current;

  return (
    <>
      <DossierCreatedTracker
        trackedRef={dossierCreatedTrackedRef}
        sawAbsentWidgetPropsRef={sawAbsentWidgetPropsRef}
        sessionId={params.sessionId}
        hasWidgetProps={Boolean(
          query.data.widgetProps && Object.keys(query.data.widgetProps).length,
        )}
      />
      {v3 ? (
        query.data.widgetProps && Object.keys(query.data.widgetProps).length ? (
          <V3ResultPage
            widgetProps={query.data.widgetProps}
            companyId={params.companyId}
            dossierCreated={dossierCreated}
          />
        ) : (
          <V3ResultCalc />
        )
      ) : (
        <RenderWidgets
          showLoadingMessage={query.data.showLoadingMessage}
          widgets={resultPageWidgets(query.data.widgetProps)}
          sessionId={params.sessionId}
        />
      )}
    </>
  );
}

/**
 * The dossier is created asynchronously on the server; the client only
 * confirms success by observing `widgetProps` finally populated (the same
 * signal `refetchInterval` above uses to stop polling).
 *
 * Fire conditions (ALL must hold):
 * 1. State transition: widgetProps were absent in this tab session before
 *    and THEN became populated. The absence is the proof of a fresh
 *    server-side creation the client is polling for. A page that mounts
 *    with widgetProps already populated is a reload, return visit or
 *    bookmark and must NOT count.
 * 2. Session guard: no event for this sessionId in this tab yet
 *    (sessionStorage, survives reload/back-navigation in the same tab).
 *    Written BEFORE firing so a crash mid-track cannot double count.
 * 3. Per-mount guard: trackedRef keeps the effect idempotent.
 */
const DOSSIER_CREATED_SESSION_KEY = 'as_dossier_created';

function DossierCreatedTracker(props: {
  readonly trackedRef: MutableRefObject<boolean>;
  readonly sawAbsentWidgetPropsRef: MutableRefObject<boolean>;
  readonly sessionId: string | undefined;
  readonly hasWidgetProps: boolean;
}) {
  useEffect(() => {
    if (!props.hasWidgetProps) {
      // Witnessed the pending state (polling before the dossier exists).
      // Stays true once seen — this ref proves the transition.
      props.sawAbsentWidgetPropsRef.current = true;
      return;
    }

    if (
      props.trackedRef.current ||
      !props.sessionId ||
      !props.sawAbsentWidgetPropsRef.current
    ) {
      return;
    }

    props.trackedRef.current = true;
    props.sawAbsentWidgetPropsRef.current = false;

    const storageKey = `${DOSSIER_CREATED_SESSION_KEY}_${props.sessionId}`;

    try {
      if (!sessionStorage.getItem(storageKey)) {
        sessionStorage.setItem(storageKey, 'true');
        trackEvent('immodossier dossier created');
      }
    } catch {
      // Storage unavailable (private mode, quota): fire via ref guard only
      trackEvent('immodossier dossier created');
    }
  }, [
    props.hasWidgetProps,
    props.trackedRef,
    props.sawAbsentWidgetPropsRef,
    props.sessionId,
  ]);

  return null;
}

function V3Error(props: {
  readonly error: string;
  readonly sessionId?: string;
  readonly address?: string;
}) {
  const { t } = useTranslate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const back = () =>
    canGoBack ? router.history.back() : void router.navigate({ to: '/' });
  const title = t('calculator_result_page_error_title');

  switch (props.error) {
    case 'already_known':
    case 'dossier_exists':
      return (
        <V3ResultError
          title={title}
          action={{
            label: t(
              'calculator_result_page_already_known_error_message_link_text',
            ),
            href: '/',
          }}
        >
          <p>
            {t(
              'calculator_result_page_already_known_error_message_prefix_text',
            )}{' '}
            <a
              href="/"
              className="v3p-link"
              data-track="immodossier valiant result already known link"
            >
              {t(
                'calculator_result_page_already_known_error_message_link_text',
              )}
            </a>{' '}
            {t(
              'calculator_result_page_already_known_error_message_postfix_text',
            )}
          </p>
        </V3ResultError>
      );
    case 'invalid_email':
      return (
        <V3ResultError
          title={title}
          action={{
            label: t('calculator_result_page_back_link'),
            onClick: back,
          }}
        >
          <p>{t('calculator_result_page_invalid_email_error_message')}</p>
        </V3ResultError>
      );
    case 'invalid_address':
      return (
        <V3ResultError
          title={title}
          action={{
            label: t('calculator_result_page_back_link'),
            onClick: back,
          }}
        >
          <p className="whitespace-pre-wrap">
            {t('calculator_result_page_invalid_address_error_message', {
              address: props.address || '',
            })}
          </p>
        </V3ResultError>
      );
    default:
      return (
        <V3ResultError
          title={title}
          action={{ label: t('calculator_result_page_login_link'), href: '/' }}
        >
          <p>{t('calculator_result_page_unknown_error_message')}</p>
          <p className="v3p-xs">
            {t('calculator_result_page_unknown_session_id', {
              value: props.sessionId || 'error',
            })}
          </p>
        </V3ResultError>
      );
  }
}

function ErrorCard(props: ErrorCardProps) {
  return (
    <div
      className={clsx(
        'h-full rounded-2xl p-5 md:p-8 max-w-[500px] shadow-sm',
        props.white
          ? 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700'
          : 'bg-red-50 dark:bg-red-900/20 border-2 border-red-600 dark:border-red-500',
      )}
    >
      <h1
        className={clsx(
          'text-2xl font-bold mb-3',
          props.white
            ? 'text-gray-900 dark:text-gray-100'
            : 'text-red-900 dark:text-red-100',
        )}
      >
        {props.title}
      </h1>
      <div
        className={clsx(
          'text-sm leading-relaxed',
          props.white
            ? 'text-gray-600 dark:text-gray-400'
            : 'text-red-800 dark:text-red-200',
        )}
      >
        {props.children}
      </div>
      {props.link ? (
        <div className="mt-5">
          <a
            href={props.link}
            data-track="immodossier valiant result error back link"
          >
            <ActionButton
              type="button"
              primary
            >
              {props.linkLabel || 'ERROR'}
            </ActionButton>
          </a>
        </div>
      ) : props.fallbackLink ? (
        <BackButtonRow
          linkLabel={props.linkLabel}
          fallbackLink={props.fallbackLink}
        />
      ) : null}
    </div>
  );
}

function BackButtonRow(props: {
  readonly linkLabel?: string;
  readonly fallbackLink: string;
}) {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <div className="mt-5">
      <ActionButton
        type="button"
        primary
        data-track="immodossier valiant result error back button"
        onClick={() => {
          if (canGoBack) {
            router.history.back();
          } else {
            void router.navigate({ to: props.fallbackLink });
          }
        }}
      >
        {props.linkLabel || 'ERROR'}
      </ActionButton>
    </div>
  );
}

interface ErrorCardProps {
  readonly title: string;
  readonly children: ReactNode;
  readonly link?: string;
  readonly linkLabel?: string;
  readonly fallbackLink?: string;
  readonly onBackClick?: () => void;
  readonly white?: boolean;
}
